import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import ClientDashboardNew from "./components/Dashboard/ClientDashboardNew";
import EngineerDashboard from "./components/Dashboard/EngineerDashboard";
import NewOrderForm from "./components/Client/NewOrderForm";
import ConsultingForm from "./components/Client/ConsultingForm";
import MyOrders from "./components/Client/MyOrders";
import OrderDetail from "./components/Client/OrderDetail";
import EngineerOrderDetail from "./components/Engineer/EngineerOrderDetail";
import EngineerConsultings from "./components/Engineer/EngineerConsultings";
import EngineerConsultingDetail from "./components/Engineer/EngineerConsultingDetail";
import MyConsultings from "./components/Client/MyConsultings";
import ConsultingDetail from "./components/Client/ConsultingDetail";
import MyPayments from "./components/Client/MyPayments";
import ClientHistory from "./components/History/ClientHistory";
import EngineerHistory from "./components/History/EngineerHistory";
import ClientStats from "./components/Stats/ClientStats";
import EngineerStats from "./components/Stats/EngineerStats";
import AdminPanel from "./components/Admin/AdminPanel";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminOrderDetail from "./pages/SuperAdminOrderDetail";
import SuperAdminConsultingDetail from "./pages/SuperAdminConsultingDetail";
import SuperAdminFileManager from "./pages/SuperAdminFileManager";
import FileManager from "./pages/FileManager";
import ProfilePage from "./pages/ProfilePage";
import { Toaster } from "./components/ui/toaster";

function App() {
  // Initialize auth state synchronously from localStorage to avoid hard-reload race
  const initialUser = (() => {
    try {
      const u = localStorage.getItem('user');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  })();
  const initialRole = localStorage.getItem('userRole') || null;
  const [isAuthenticated, setIsAuthenticated] = useState(!!(initialUser && initialRole));
  const [userRole, setUserRole] = useState(initialRole);
  const [user, setUser] = useState(initialUser);

  useEffect(() => {
    // Re-sync in case localStorage was updated by another tab
    const storedUser = localStorage.getItem('user');
    const storedRole = localStorage.getItem('userRole');
    if (storedUser && storedRole && !isAuthenticated) {
      setUser(JSON.parse(storedUser));
      setUserRole(storedRole);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (role, userData) => {
    setIsAuthenticated(true);
    setUserRole(role);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userRole', role);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
  };

  const ProtectedRoute = ({ children, allowedRole }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (allowedRole && userRole !== allowedRole) {
      return <Navigate to="/" />;
    }
    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Navbar 
          isAuthenticated={isAuthenticated} 
          userRole={userRole}
          onLogout={handleLogout}
        />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/register" element={<Register onLogin={handleLogin} />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Payment Routes */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<SuperAdminDashboard />} />
          <Route path="/super-admin/orders/:orderId" element={<SuperAdminOrderDetail />} />
          <Route path="/super-admin/consultings/:consultingId" element={<SuperAdminConsultingDetail />} />
          <Route path="/super-admin/file-manager" element={<SuperAdminFileManager />} />
          
          <Route 
            path="/client/dashboard" 
            element={
              <ProtectedRoute allowedRole="client">
                <ClientDashboardNew user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/new-order" 
            element={
              <ProtectedRoute allowedRole="client">
                <NewOrderForm user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/consulting" 
            element={
              <ProtectedRoute allowedRole="client">
                <ConsultingForm user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/orders" 
            element={
              <ProtectedRoute allowedRole="client">
                <MyOrders user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/orders/:orderId" 
            element={
              <ProtectedRoute allowedRole="client">
                <OrderDetail user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/consultings" 
            element={
              <ProtectedRoute allowedRole="client">
                <MyConsultings user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/consultings/:consultingId" 
            element={
              <ProtectedRoute allowedRole="client">
                <ConsultingDetail user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/payments" 
            element={
              <ProtectedRoute allowedRole="client">
                <MyPayments user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/history" 
            element={
              <ProtectedRoute allowedRole="client">
                <ClientHistory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/stats" 
            element={
              <ProtectedRoute allowedRole="client">
                <ClientStats />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/dashboard" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerDashboard user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/orders/:orderId" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerOrderDetail user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/orders" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerDashboard user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/history" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerHistory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/stats" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerStats />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/profile" 
            element={
              <ProtectedRoute allowedRole="client">
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/profile" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/admin" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <AdminPanel />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/consultings" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerConsultings user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/consultings/:consultingId" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <EngineerConsultingDetail user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/engineer/file-manager" 
            element={
              <ProtectedRoute allowedRole="engineer">
                <FileManager user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/superadmin/file-manager" 
            element={
              <FileManager user={{ role: "admin" }} />
            } 
          />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
